import {ChevronRight, Settings, X, Lock, FileText, LogOut, User} from 'lucide-react'
import React, {useEffect, useRef, useState, useCallback} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {useGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {useGetOneCompanyQuery} from '~/src/store/apis/company-api'
import {useSignoutMutation} from '~/src/store/apis/auth-api'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import type {INavMenuItem} from '../../layout/MenuItems'
import GlobalDropdown from './GlobalDropdown'
import {JobSourceLogo, LogoText} from './JobSourceIcon'
import NavMenuItem from './NavMenuItem'
import SelectAgencyMenu from './SelectAgencyMenu'
import SubMenu from './SubMenu'
import {useActiveLink} from './useActiveLink'
import UserMenu from './UserMenu'
import ChangePasswordDialog from '../dialog/ChangePasswordDialog'
import EulaDialog from './EulaDialog'
import {Tooltip} from '@mui/material'
import {useFilteredMenuItems} from '~/src/hooks/useFilteredMenuItems'
import {usePermission} from '~/src/hooks/usePermission'

interface NavMenuBarProps {
  items: INavMenuItem[]
  variant?: 'platform' | 'msp' | 'main-agency' | 'agency' | 'company' | 'general-admin'
}

// ==================== STYLED COMPONENTS ====================

const MOBILE_BREAKPOINT = '1000px'

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`

const NavInner = styled.div`
  height: 100%;
  padding: 0 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 16px;
  }
`

const LogoContainer = styled.div<{$useBlock: boolean}>`
  display: ${({$useBlock}) => ($useBlock ? 'block' : 'flex')};
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 1rem;
  color: #334d6e;
  flex-shrink: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 150px;
    font-size: 14px;
  }
`

const DesktopNavItems = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: 12px;
  }
`

const MobileSettingsButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #334d6e;
  transition: color 0.2s;

  &:hover {
    color: #03a9fc;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const DesktopUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const AssignedMspText = styled.div`
  font-size: 11px;
  line-height: 13px;
  color: #8a8a8a;
  text-align: right;
  margin-left: 69px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

// ==================== DRAWER STYLES ====================

const DrawerOverlay = styled.div<{$isOpen: boolean}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s, visibility 0.3s;
`

const Drawer = styled.div<{$isOpen: boolean}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 85vw;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  transform: translateX(${(props) => (props.$isOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #ebebeb;
  flex-shrink: 0;
`

const DrawerTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #334d6e;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #334d6e;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

const DrawerContent = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DrawerSection = styled.div`
  margin-bottom: 24px;
`

const DrawerSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8a8a8a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding: 0 12px;
`

const DrawerNavItem = styled.button<{$active: boolean; $hasSubMenu: boolean}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: ${(props) => (props.$active ? 'rgba(3, 169, 252, 0.08)' : 'transparent')};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: ${(props) => (props.$active ? '#03a9fc' : '#334d6e')};
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.04);
    color: #03a9fc;
  }
`

const DrawerSubMenu = styled.div<{$isExpanded: boolean}>`
  display: ${(props) => (props.$isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  padding-left: 24px;
  margin-top: 4px;
`

const DrawerSubMenuItem = styled.button<{$active: boolean}>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: ${(props) => (props.$active ? 'rgba(3, 169, 252, 0.08)' : 'transparent')};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: ${(props) => (props.$active ? '#03a9fc' : '#334d6e')};
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.04);
    color: #03a9fc;
  }
`

const DrawerUserSection = styled.div`
  padding: 16px;
  border-top: 1px solid #ebebeb;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
`

const ChevronIcon = styled(ChevronRight)<{$isExpanded: boolean}>`
  transition: transform 0.2s;
  transform: ${(props) => (props.$isExpanded ? 'rotate(90deg)' : 'rotate(0)')};
  flex-shrink: 0;
`

// ==================== COMPONENT ====================

const NavMenuBar = ({items, variant}: NavMenuBarProps) => {
  // Filter menu items based on user permissions
  const filteredItems = useFilteredMenuItems(items)
  const {activeLink, handleSetActive, subMenuItems} = useActiveLink(filteredItems)
  const {currentUser} = useCurrentUser()
  const {companyId, roles} = currentUser || {companyId: '', roles: [] as string[]}
  const company = useGetOneCompanyQuery(companyId, {
    skip: !companyId,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [signout] = useSignoutMutation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({})
  const [isEulaOpen, setIsEulaOpen] = useState(false)
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  const foundItem = filteredItems.find((item) => {
    let val = false
    if (item.link) {
      const pattern = item.activePattern || item.link
      val = location.pathname.search(new RegExp(pattern, 'i')) !== -1
    } else {
      const foundSubItem = item.subMenuItems.find((subItem) => {
        const subPattern = subItem.activePattern || (subItem.link as string)
        const subVal = location.pathname.search(new RegExp(subPattern, 'i')) !== -1
        return subVal
      })
      val = foundSubItem !== undefined
    }
    return val
  })

  const agency = useGetOneAgencyQuery(company.currentData?.agencyId ?? '', {
    skip: !company.currentData?.agencyId,
  })

  const ref = useRef<HTMLElement>(null)

  // Determine if current URL matches a parent with sub-items (should keep sub-menu open)
  const activeParentWithSubMenu = filteredItems.find((item) => {
    if (!item.subMenuItems || item.subMenuItems.length === 0) return false
    return item.subMenuItems.some((subItem) => {
      const subPattern = subItem.activePattern || (subItem.link as string)
      return location.pathname.search(new RegExp(subPattern, 'i')) !== -1
    })
  })

  // Get the sub-menu items for the active parent
  const activeSubMenuItems = activeParentWithSubMenu?.subMenuItems || []

  // Track manually opened sub-menu (for clicking different parent)
  const [manuallyOpenedParentId, setManuallyOpenedParentId] = useState<number | null>(null)

  // Sub-menu is open if: URL matches a parent with sub-items OR user manually clicked a parent
  const isSubMenuOpen = activeParentWithSubMenu !== undefined || manuallyOpenedParentId !== null

  // Determine which sub-items to show
  const displaySubMenuItems =
    manuallyOpenedParentId !== null
      ? filteredItems.find((item) => item.id === manuallyOpenedParentId)?.subMenuItems || []
      : activeSubMenuItems

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false)
    // Clear manually opened parent when navigating (URL-based will take over)
    setManuallyOpenedParentId(null)
  }, [location.pathname])

  // Close submenu when clicking outside (desktop only)
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isSubMenuOpen && ref.current && !ref.current.contains(e.target as Node)) {
        setManuallyOpenedParentId(null)
      }
    }

    document.addEventListener('mousedown', checkIfClickedOutside)
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [isSubMenuOpen])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const id = parseInt(event.currentTarget.id, 10)
    const clickedItem = filteredItems.find((item) => item.id === id)

    handleSetActive(event)

    // If clicked item has sub-menu, open it
    if (clickedItem?.subMenuItems && clickedItem.subMenuItems.length > 0) {
      setManuallyOpenedParentId(id)
    } else {
      setManuallyOpenedParentId(null)
    }
  }

  const handleSubMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // When clicking a sub-menu item, just navigate - don't close the sub-menu
    handleSetActive(event)
    // Clear manual override so URL-based detection takes over
    setManuallyOpenedParentId(null)
  }

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const toggleExpandItem = (itemId: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  const handleSignOut = async () => {
    if (!currentUser) return
    await signout({email: currentUser.emailAddress}).unwrap()
    navigate('/signin')
  }

  const handleOpenEula = useCallback(() => {
    setIsDrawerOpen(false)
    setIsEulaOpen(true)
  }, [])

  const handleCloseEula = useCallback(() => {
    setIsEulaOpen(false)
  }, [])

  const handleOpenChangePassword = useCallback(() => {
    console.log('handleOpenChangePassword')
    setIsDrawerOpen(false)
    setIsChangePasswordOpen(true)
  }, [])

  const handleCloseChangePassword = useCallback(() => {
    setIsChangePasswordOpen(false)
  }, [])

  const handleDrawerNavClick = (item: INavMenuItem) => {
    if (item.subMenuItems && item.subMenuItems.length > 0) {
      toggleExpandItem(item.id)
    } else {
      // Navigate to the link
      window.location.href = item.link as string
    }
  }

  const handleDrawerSubNavClick = (link: string) => {
    window.location.href = link
  }

  // Check if user has global module permission (for showing Global dropdown)
  const globalPermission = usePermission('global')
  const hasGlobalAccess = globalPermission.canView

  // Determine variant-specific flags
  const isPlatformVariant = variant === 'platform'
  const isGeneralAdmin = variant === 'general-admin' && roles?.includes('general_admin')

  // Get the user's role category from backend permissions
  const backendRolePermissions = useAppSelector((state) => state.root.backendRolePermissions)
  const roleCategory = backendRolePermissions?.category || backendRolePermissions?.application

  // Determine if user should see agency switcher:
  // Only show for MSP and Agency category roles (not for general_admin, Platform, or Company)
  // User must also have multiple agency assignments
  const isMspOrAgencyCategory =
    roleCategory?.toLowerCase() === 'msp' || roleCategory?.toLowerCase() === 'agency'
  const hasMultipleAgencies =
    isMspOrAgencyCategory &&
    currentUser?.agencyAssignments &&
    currentUser.agencyAssignments.length > 1

  return (
    <>
      <NavContainer ref={ref}>
        <NavInner>
          {/* Logo / Company Name */}
          <LogoContainer $useBlock={company?.currentData?.name}>
            {company.currentData?.name ? (
              <Tooltip title={company.currentData.name}>
                <span>{company.currentData.name}</span>
              </Tooltip>
            ) : (
              <>
                <JobSourceLogo />
                <LogoText />
              </>
            )}
          </LogoContainer>

          {/* Desktop Navigation Items */}
          <DesktopNavItems>
            {filteredItems?.map((page, index) => {
              const hasSubMenu = page.subMenuItems && page.subMenuItems.length > 0
              const firstSubMenuLink =
                hasSubMenu && page.subMenuItems ? page.subMenuItems[0].link : undefined
              const subMenuLinks =
                hasSubMenu && page.subMenuItems
                  ? page.subMenuItems.map((item) => item.link ?? '')
                  : []

              // For platform variant with global access, insert Global dropdown before Payroll (index 3)
              if (isPlatformVariant && hasGlobalAccess && index === 3) {
                return (
                  <React.Fragment key={`global-${page.id}`}>
                    <div style={{marginLeft: index > 0 ? '8px' : '0', marginRight: '8px'}}>
                      <GlobalDropdown
                        active={
                          location.pathname.includes('/global') ||
                          location.pathname.includes('/roles-permissions')
                        }
                      />
                    </div>
                    <NavMenuItem
                      id={page.id.toString()}
                      key={page.id}
                      text={page.text}
                      link={page.link ?? location.pathname}
                      active={page.id === foundItem?.id}
                      handleSetActive={handleClick}
                      hasSubMenu={hasSubMenu}
                      firstSubMenuLink={firstSubMenuLink}
                      subMenuLinks={subMenuLinks}
                    />
                  </React.Fragment>
                )
              }

              return (
                <NavMenuItem
                  id={page.id.toString()}
                  key={page.id}
                  text={page.text}
                  link={page.link ?? location.pathname}
                  active={page.id === foundItem?.id}
                  handleSetActive={handleClick}
                  hasSubMenu={hasSubMenu}
                  firstSubMenuLink={firstSubMenuLink}
                  subMenuLinks={subMenuLinks}
                />
              )
            })}
            {/* Show Global dropdown for general_admin after nav items */}
            {isGeneralAdmin && (
              <div style={{marginLeft: '8px'}}>
                <GlobalDropdown
                  active={
                    location.pathname.includes('/global') ||
                    location.pathname.includes('/roles-permissions')
                  }
                />
              </div>
            )}
          </DesktopNavItems>

          {/* Right Section */}
          <RightSection>
            {/* Desktop User Section */}
            <DesktopUserSection>
              {hasMultipleAgencies && (
                <div style={{marginRight: '20px'}}>
                  <SelectAgencyMenu />
                </div>
              )}
              {currentUser && <UserMenu currentUser={currentUser} />}
              {agency.currentData && (
                <AssignedMspText>
                  Assigned MSP <br />
                  {agency.currentData.name}
                </AssignedMspText>
              )}
            </DesktopUserSection>

            {/* Mobile Settings Button */}
            <MobileSettingsButton onClick={toggleDrawer} aria-label="Open settings menu">
              <Settings size={24} />
            </MobileSettingsButton>
          </RightSection>
        </NavInner>

        {/* Desktop SubMenu - stays open when URL matches a sub-item */}
        {isSubMenuOpen && displaySubMenuItems.length > 0 ? (
          <SubMenu
            items={displaySubMenuItems}
            closeMenu={handleSubMenuClick}
            activeSubLink={activeLink.subMenuId}
            currentPath={location.pathname}
          />
        ) : null}
      </NavContainer>

      {/* Mobile Drawer */}
      <DrawerOverlay $isOpen={isDrawerOpen} onClick={toggleDrawer} />
      <Drawer $isOpen={isDrawerOpen}>
        <DrawerHeader>
          <DrawerTitle>Menu</DrawerTitle>
          <CloseButton onClick={toggleDrawer} aria-label="Close menu">
            <X size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {/* Navigation Section */}
          <DrawerSection>
            <DrawerSectionTitle>Navigation</DrawerSectionTitle>
            {filteredItems?.map((page, index) => {
              const hasSubMenu = page.subMenuItems && page.subMenuItems.length > 0
              const isActive = page.id === foundItem?.id
              const isExpanded = expandedItems[page.id] || false

              // Add Global dropdown for platform variant with global access
              if (isPlatformVariant && hasGlobalAccess && index === 3) {
                return (
                  <React.Fragment key={`global-drawer-${page.id}`}>
                    <DrawerNavItem
                      $active={
                        location.pathname.includes('/global') ||
                        location.pathname.includes('/roles-permissions')
                      }
                      $hasSubMenu={false}
                      onClick={() => (window.location.href = '/global')}>
                      Global
                    </DrawerNavItem>
                    <DrawerNavItem
                      $active={isActive}
                      $hasSubMenu={hasSubMenu}
                      onClick={() => handleDrawerNavClick(page)}>
                      {page.text}
                      {hasSubMenu && <ChevronIcon size={16} $isExpanded={isExpanded} />}
                    </DrawerNavItem>
                    {hasSubMenu && (
                      <DrawerSubMenu $isExpanded={isExpanded}>
                        {page.subMenuItems?.map((subItem) => {
                          const subPattern = subItem.activePattern || (subItem.link as string)
                          const isSubActive =
                            location.pathname.search(new RegExp(subPattern, 'i')) !== -1

                          return (
                            <DrawerSubMenuItem
                              key={subItem.id}
                              $active={isSubActive}
                              onClick={() => handleDrawerSubNavClick(subItem.link as string)}>
                              {subItem.text}
                            </DrawerSubMenuItem>
                          )
                        })}
                      </DrawerSubMenu>
                    )}
                  </React.Fragment>
                )
              }

              return (
                <React.Fragment key={page.id}>
                  <DrawerNavItem
                    $active={isActive}
                    $hasSubMenu={hasSubMenu}
                    onClick={() => handleDrawerNavClick(page)}>
                    {page.text}
                    {hasSubMenu && <ChevronIcon size={16} $isExpanded={true} />}
                  </DrawerNavItem>
                  {hasSubMenu && (
                    <DrawerSubMenu $isExpanded={true}>
                      {page.subMenuItems?.map((subItem) => {
                        const subPattern = subItem.activePattern || (subItem.link as string)
                        const isSubActive =
                          location.pathname.search(new RegExp(subPattern, 'i')) !== -1

                        return (
                          <DrawerSubMenuItem
                            key={subItem.id}
                            $active={isSubActive}
                            onClick={() => handleDrawerSubNavClick(subItem.link as string)}>
                            {subItem.text}
                          </DrawerSubMenuItem>
                        )
                      })}
                    </DrawerSubMenu>
                  )}
                </React.Fragment>
              )
            })}
            {/* Add Global menu item for general_admin in mobile drawer */}
            {isGeneralAdmin && (
              <DrawerNavItem
                $active={location.pathname.includes('/roles-permissions')}
                $hasSubMenu={false}
                onClick={() => (window.location.href = '/roles-permissions')}>
                Global
              </DrawerNavItem>
            )}
          </DrawerSection>

          {/* Settings Section */}
          <DrawerSection>
            <DrawerSectionTitle>Settings</DrawerSectionTitle>

            {/* User Info */}
            {currentUser && (
              <DrawerNavItem $active={false} $hasSubMenu={false} style={{cursor: 'default'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <User size={16} />
                  <span>{currentUser.emailAddress}</span>
                </div>
              </DrawerNavItem>
            )}

            {/* Change Password */}
            <DrawerNavItem $active={false} $hasSubMenu={false} onClick={handleOpenChangePassword}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Lock size={16} />
                <span>Change Password</span>
              </div>
            </DrawerNavItem>

            {/* EULA */}
            <DrawerNavItem $active={false} $hasSubMenu={false} onClick={handleOpenEula}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <FileText size={16} />
                <span>Eula</span>
              </div>
            </DrawerNavItem>

            {/* Logout */}
            <DrawerNavItem $active={false} $hasSubMenu={false} onClick={() => void handleSignOut()}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626'}}>
                <LogOut size={16} />
                <span>Log Out</span>
              </div>
            </DrawerNavItem>
          </DrawerSection>
        </DrawerContent>

        {/* Drawer Footer - Agency Info */}
        <DrawerUserSection>
          {hasMultipleAgencies && <SelectAgencyMenu />}
          {agency.currentData && (
            <div style={{fontSize: '11px', color: '#8a8a8a', textAlign: 'center'}}>
              Assigned MSP: {agency.currentData.name}
            </div>
          )}
        </DrawerUserSection>
      </Drawer>

      {/* Dialogs */}
      {currentUser && (
        <>
          <ChangePasswordDialog
            isOpen={isChangePasswordOpen}
            handleCancel={handleCloseChangePassword}
          />
          <EulaDialog isModalOpen={isEulaOpen} onModalClose={handleCloseEula} />
        </>
      )}
    </>
  )
}

export default NavMenuBar
